import { type FC, useState } from 'react'
import { Chip } from '@/components/commons/chip'
import { GalleryModal } from '@/components/commons/gallery-modal'
import { GalleryModalHeader } from '@/components/commons/gallery-modal-header'
import { GalleryModalThumbnails } from '@/components/commons/gallery-modal-thumbnails'
import { AlertTriangleIcon, CheckIcon } from '@/components/ui/icons'
import { Separator } from '@/components/ui/separator'
import {
	OpsVettingDisabledContainer,
	OpsVettingGalleryGrid,
	OpsVettingParagraph,
	OpsVettingSectionTitle,
	OpsVettingSectionWrapper
} from '@/features/ops-vetting/components/ops-vetting-atoms'
import { useGalleryNeedsReviewShortcuts } from '@/features/photoset/hooks/use-gallery-needs-review-shortcuts'
import { useServiceVettingError } from '@/features/service-vetting/store/use-service-vetting-error.ts'
import { useTranslation } from '@/translations/i18n'
import type { ServiceGalleryImage } from '../types'
import { ServiceVettingCriteriaRejectedImageItem } from './service-vetting-criteria-rejected-image-item'

interface ServiceVettingCriteriaRejectedImagesProps {
	rejectedImages: ServiceGalleryImage[]
	hideAction: boolean
	isExpertiseDeclined: boolean
	galleryId: number
}

export const ServiceVettingCriteriaRejectedImages: FC<
	ServiceVettingCriteriaRejectedImagesProps
> = ({ rejectedImages, hideAction, isExpertiseDeclined, galleryId }) => {
	const { t } = useTranslation()
	const [expandedImageIndex, setExpandedImageIndex] = useState<number | null>(
		null
	)
	const [showGalleryModal, setShowGalleryModal] = useState(false)

	const errors = useServiceVettingError((state) => state.errors)

	const handleClickExpandedImage = (imageId: ServiceGalleryImage['id']) => {
		const index = rejectedImages.findIndex((image) => image.id === imageId)
		setExpandedImageIndex(index)
		setShowGalleryModal(true)
	}

	const handleCloseModal = () => {
		setShowGalleryModal(false)
		setExpandedImageIndex(null)
	}

	useGalleryNeedsReviewShortcuts({
		selectableImages: rejectedImages,
		currentDetailImageIndex: expandedImageIndex || 0,
		setCurrentDetailImageIndex: setExpandedImageIndex,
		onClose: handleCloseModal
	})

	return (
		<OpsVettingSectionWrapper direction={'column'} fullWidth gap={6}>
			<div className='flex w-full items-center justify-between'>
				<OpsVettingSectionTitle>
					{t('vetting:criteria.commons.rejected_images.title')}
				</OpsVettingSectionTitle>
				<div className='flex items-center gap-4'>
					{errors.rejectedImagesErrors.map((error) => (
						<Chip
							isActive
							key={error}
							label={t(error)}
							startSlot={<AlertTriangleIcon />}
							variant={'danger'}
						/>
					))}
					<OpsVettingParagraph>
						{rejectedImages.filter(
							(image) => image.flags.length === 0
						).length > 0 ? (
							t(
								'vetting:criteria.commons.rejected_images.images_remaining_flags',
								{
									count: rejectedImages.filter(
										(image) => image.flags.length === 0
									).length
								}
							)
						) : (
							<span className='flex gap-1'>
								<CheckIcon
									className='fill-success-600'
									size={20}
								/>
								{t(
									'vetting:criteria.commons.rejected_images.images_remaining_no_flags'
								)}
							</span>
						)}
					</OpsVettingParagraph>
				</div>
			</div>
			<Separator orientation='horizontal' />
			<OpsVettingDisabledContainer $isDisabled={isExpertiseDeclined}>
				<OpsVettingGalleryGrid>
					{rejectedImages.map((image) => (
						<ServiceVettingCriteriaRejectedImageItem
							galleryId={galleryId}
							hideAction={hideAction}
							image={image}
							key={image.id}
							onClickExpand={() =>
								handleClickExpandedImage(image.id)
							}
						/>
					))}
				</OpsVettingGalleryGrid>
			</OpsVettingDisabledContainer>

			{showGalleryModal && expandedImageIndex !== null && (
				<GalleryModal
					currentImage={rejectedImages[expandedImageIndex]}
					headerSlot={
						<GalleryModalHeader
							currentImage={rejectedImages[expandedImageIndex]}
							imagesCount={rejectedImages.length}
							onClose={handleCloseModal}
						/>
					}
					onClose={handleCloseModal}
					thumbnails={{
						l: rejectedImages[expandedImageIndex].url || '',
						m: '',
						s: '',
						xs: ''
					}}
					thumbnailsSlot={
						<GalleryModalThumbnails
							currentImageId={
								rejectedImages[expandedImageIndex].id
							}
							images={rejectedImages.map((image) => ({
								id: image.id,
								name: image.name,
								thumbnails: {
									l: image.url || '',
									m: image.url || '',
									s: image.url || '',
									xs: image.url || ''
								}
							}))}
							onSelect={(_, index) => {
								setExpandedImageIndex(index)
							}}
							thumbnails='thumbnails'
						/>
					}
				/>
			)}
		</OpsVettingSectionWrapper>
	)
}
